import { useEffect, useState } from "react";
import { Button, Typography, Space, Slider, Tooltip } from "antd";
import { LockOutlined, UnlockOutlined } from "@ant-design/icons";
import chevronUpIcon from "../assets/ui_revamp/Chevron Up.svg";
import { DrawingModes } from "../ThreeJs/Global/variables";
import { pushUndoAction } from "../ThreeJs/Global/UndoManager";
import { notifyIrradianceNeedsRecompute } from "../ThreeJs/Shading/IrradianceEvents";
import sphereIcon from "../assets/ui_revamp/Sphere.svg";
import deleteButtonIcon from "../assets/ui_revamp/DeleteButton.svg";

const { Text } = Typography;

const RemovedSections = ({
  removedSections,
  setRemovedSections,
  updateDrawingMode,
  selectedSphereFromScene,
  irradianceBusy = false,
}) => {
  const [expandedSphereIndex, setExpandedSphereIndex] = useState(null);
  const spheres = removedSections?.spheres || [];

  const sphereRows = spheres.map((sphere, index) => ({
    sphere,
    index,
    isExpanded: expandedSphereIndex === index,
    radius: (sphere?.scale?.x || 0).toFixed(2),
  }));

  const applySphereScale = (sphere, scale) => {
    if (!sphere || !scale) return;
    sphere.scale.set(scale.x, scale.y, scale.z);
  };

  const handleSphereChange = (index, key, value) => {
    if (irradianceBusy) return;
    if (typeof value !== "number" || Number.isNaN(value)) return;
    setRemovedSections((prev) => {
      const updated = [...(prev?.spheres || [])];
      const sphere = updated[index];
      if (!sphere || sphere.userData?.locked) return prev;
      const before = {
        x: sphere.scale.x,
        y: sphere.scale.y,
        z: sphere.scale.z,
      };

      if (key === "radius") {
        sphere.scale.set(value, value, value);
      } else if (key === "width") sphere.scale.x = value;
      else if (key === "length") sphere.scale.y = value;
      else if (key === "height") sphere.scale.z = value;

      const after = {
        x: sphere.scale.x,
        y: sphere.scale.y,
        z: sphere.scale.z,
      };
      if (
        Math.abs(before.x - after.x) > 1e-6 ||
        Math.abs(before.y - after.y) > 1e-6 ||
        Math.abs(before.z - after.z) > 1e-6
      ) {
        pushUndoAction({
          label: "Update sphere",
          undo: () => {
            applySphereScale(sphere, before);
            setRemovedSections((p) => ({ ...p, spheres: [...(p?.spheres || [])] }));
            notifyIrradianceNeedsRecompute("trim-sphere-updated");
          },
          redo: () => {
            applySphereScale(sphere, after);
            setRemovedSections((p) => ({ ...p, spheres: [...(p?.spheres || [])] }));
            notifyIrradianceNeedsRecompute("trim-sphere-updated");
          },
        });
        notifyIrradianceNeedsRecompute("trim-sphere-updated");
      }

      return { ...prev, spheres: updated };
    });
  };

  const handleSphereSelect = (index, sphere) => {
    const nextIndex = expandedSphereIndex === index ? null : index;
    setExpandedSphereIndex(nextIndex);
    window.attachControlsToSphere?.(sphere);
  };

  const handleDeleteSphere = (sphere) => {
    if (irradianceBusy) return;
    if (!sphere) return;
    window.removeSphereAndControls?.(sphere, { recordUndo: true });
  };

  const toggleSphereLock = (sphere) => {
    if (irradianceBusy) return;
    if (!sphere) return;
    const beforeLocked = !!sphere.userData?.locked;
    sphere.userData.locked = !sphere.userData?.locked;
    window.attachControlsToSphere?.(sphere);
    setRemovedSections((prev) => ({ ...prev, spheres: [...(prev?.spheres || [])] }));
    notifyIrradianceNeedsRecompute("trim-sphere-lock");
    const afterLocked = !!sphere.userData?.locked;
    if (beforeLocked !== afterLocked) {
      pushUndoAction({
        label: "Toggle sphere lock",
        undo: () => {
          sphere.userData.locked = beforeLocked;
          window.attachControlsToSphere?.(sphere);
          setRemovedSections((prev) => ({ ...prev, spheres: [...(prev?.spheres || [])] }));
          notifyIrradianceNeedsRecompute("trim-sphere-lock");
        },
        redo: () => {
          sphere.userData.locked = afterLocked;
          window.attachControlsToSphere?.(sphere);
          setRemovedSections((prev) => ({ ...prev, spheres: [...(prev?.spheres || [])] }));
          notifyIrradianceNeedsRecompute("trim-sphere-lock");
        },
      });
    }
  };

  useEffect(() => {
    const onSphereSelected = (event) => {
      const selectedSphere = event?.detail?.sphere;
      if (!selectedSphere) return;

      const idx = spheres.findIndex((sphere) => sphere === selectedSphere);
      if (idx === -1) return;

      setExpandedSphereIndex(idx);
      window.attachControlsToSphere?.(selectedSphere);
    };

    window.addEventListener("TRIM_SPHERE_SELECTED", onSphereSelected);
    return () => window.removeEventListener("TRIM_SPHERE_SELECTED", onSphereSelected);
  }, [spheres]);

  useEffect(() => {
    if (!selectedSphereFromScene) return;
    const idx = spheres.findIndex((sphere) => sphere === selectedSphereFromScene);
    if (idx === -1) return;
    setExpandedSphereIndex(idx);
    window.attachControlsToSphere?.(selectedSphereFromScene);
  }, [selectedSphereFromScene, spheres]);

  return (
    <div className="trim-sections-card">
      <div className="segment-toolbar tree-toolbar trim-sections-toolbar">
        <Button
          block
          type="primary"
          className="segment-add-button"
          disabled={irradianceBusy}
          onClick={() => updateDrawingMode?.(DrawingModes.TRIM)}
        >
          Add Excluder
        </Button>
      </div>

      <div className="segment-list trim-sphere-list">
        {sphereRows.length ? (
          sphereRows.map(({ sphere, index, isExpanded, radius }) => (
            <div
              key={sphere?.uuid || index}
              className={`segment-pill trim-sphere-pill ui-card ${isExpanded ? "is-expanded" : ""}`}
            >
              <Text
                strong
                className="segment-pill-title"
                role="button"
                tabIndex={0}
                aria-expanded={isExpanded}
                onClick={() => handleSphereSelect(index, sphere)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    handleSphereSelect(index, sphere);
                  }
                }}
              >
                <span className="segment-pill-text">
                  <span className="sphere-pill-chip" aria-hidden="true">
                    <img src={sphereIcon} alt="" className="sphere-pill-icon" aria-hidden="true" />
                  </span>
                  <span className="sphere-pill-copy">
                    <span className="segment-pill-label">Excluder {index + 1}</span>
                    <span className="segment-pill-subtext">Radius: {radius}m</span>
                  </span>
                </span>
              </Text>

              <div className="segment-pill-actions">
                <Tooltip title={sphere.userData?.locked ? "Unlock excluder" : "Lock excluder"}>
                  <Button
                    type="text"
                    size="small"
                    disabled={irradianceBusy}
                    icon={sphere.userData?.locked ? <UnlockOutlined /> : <LockOutlined />}
                    onClick={(event) => {
                      event.stopPropagation();
                      toggleSphereLock(sphere);
                    }}
                  />
                </Tooltip>

                <Tooltip title="Delete excluder">
                  <Button
                    danger
                    type="text"
                    size="small"
                    disabled={irradianceBusy}
                    icon={<img src={deleteButtonIcon} alt="Delete" className="delete-icon-img" />}
                    onClick={(event) => {
                      event.stopPropagation();
                      handleDeleteSphere(sphere);
                    }}
                  />
                </Tooltip>

                <img
                  src={chevronUpIcon}
                  alt="Toggle excluder"
                  className={`segment-pill-caret ${isExpanded ? "is-expanded" : ""}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleSphereSelect(index, sphere)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      handleSphereSelect(index, sphere);
                    }
                  }}
                />
              </div>

              {isExpanded && (
                <div className="segment-pill-body trim-sphere-body">
                  <div className="trim-input-row">
                    <Space style={{ width: "100%", justifyContent: "space-between" }}>
                      <Text type="secondary">Radius</Text>
                      <Text className="trim-value-inline">{(sphere.scale.x || 0).toFixed(2)}</Text>
                    </Space>
                    <Slider
                      min={0.1}
                      max={20}
                      step={0.05}
                      value={sphere.scale.x}
                      onChange={(val) => handleSphereChange(index, "radius", val)}
                      disabled={!!sphere.userData?.locked || irradianceBusy}
                    />
                  </div>

                  <div className="trim-dim-list">
                    {[
                      { label: "Width", key: "width", value: sphere.scale.x },
                      { label: "Length", key: "length", value: sphere.scale.y },
                      { label: "Height", key: "height", value: sphere.scale.z },
                    ].map((dim) => (
                      <div key={dim.key} className="trim-dim-item">
                        <Space style={{ width: "100%", justifyContent: "space-between" }}>
                          <Text type="secondary" className="trim-dim-label">
                            {dim.label}
                          </Text>
                          <Text className="trim-value-inline">{(dim.value || 0).toFixed(0)}</Text>
                        </Space>
                        <Slider
                          min={0.1}
                          max={20}
                          step={0.05}
                          value={dim.value}
                          onChange={(val) => handleSphereChange(index, dim.key, val)}
                          disabled={!!sphere.userData?.locked || irradianceBusy}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="segment-empty-state tree-empty-state trim-empty-state">
            <div className="segment-empty-title">No Excluders</div>
            <div className="segment-empty-subtitle">
              Add an excluder to control exclusion zones in the model.
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RemovedSections;
